import React from "react";
import {
  FiAlertCircle,
  FiBox,
  FiBriefcase,
  FiCheckSquare,
  FiClock,
  FiKey,
  FiLayers,
  FiSmartphone,
  FiUser,
  FiUserMinus,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import {
  Code,
  Note,
  Section,
  Table,
  Tbody,
  TD,
  Thead,
} from "../../../../pages/KnowledgeCenterPage/ApiDocs/primitives";
import {
  Arrow,
  Diagram,
  GuideCard,
  GuideLink,
  Lead,
  List,
  Node,
  NodeItem,
  NumberedList,
  TabBadge,
} from "../primitives";

export const StartHere: React.FC = () => (
  <div className="space-y-8">
    <Lead>
      Use this page when you set up or change who can see what in AMP. Pick a job below, or read How access
      works first if the words role, endpoint group and asset group are new to you.
    </Lead>

    <Section title="Most common jobs">
      <div className="grid gap-3 md:grid-cols-2">
        <GuideCard to="onboarding-client" icon={FiBriefcase} title="Give a new client access">
          Company, asset group, users and a role, in that order.
        </GuideCard>
        <GuideCard to="onboarding-user" icon={FiUserPlus} title="Add a person to an existing client">
          Invite them in User Setup and tick the client's role.
        </GuideCard>
        <GuideCard to="onboarding-an-asset" icon={FiCheckSquare} title="Make a new asset visible and upload-ready">
          Assets &gt; Onboarding: New asset, then Fix all.
        </GuideCard>
        <GuideCard to="mobile-users" icon={FiSmartphone} title="Give someone the mobile app">
          One mobile role per asset.
        </GuideCard>
        <GuideCard to="troubleshooting" icon={FiAlertCircle} title="Find out why someone gets a 403">
          Or can't see an asset. Symptoms, causes and fixes.
        </GuideCard>
        <GuideCard to="when-changes-apply" icon={FiClock} title="Make a saved change show up now">
          Refresh access for one user.
        </GuideCard>
        <GuideCard to="offboarding" icon={FiUserMinus} title="Remove someone's access">
          Disable, remove a role, or find inactive users.
        </GuideCard>
        <GuideCard to="method-access" icon={FiKey} title="Fix an API's path or method level">
          Stored paths have their ids removed.
        </GuideCard>
      </div>
    </Section>

    <Section title="Who this is for">
      <List>
        <li>AMP admins change things on these pages.</li>
        <li>
          The IT group has read access. The newer tabs (Permission Objects, Access Explorer, Onboarding) hide
          write buttons for IT. Some older tabs still show New and Edit buttons to IT. If you are in the IT
          group, treat every tab as read-only.
        </li>
      </List>
    </Section>

    <Section title="What the admin pages are for">
      <List>
        <li>
          The <strong>Assets</strong> tabs hold the records: assets, companies, participants and resources.
          Onboarding checks that a new asset is ready.
        </li>
        <li>
          The <strong>Entitlements</strong> tabs decide who sees what: users, roles, endpoint groups, permission
          objects and asset groups. Access Explorer shows the result for one role or one user.
        </li>
      </List>
      <p>
        <GuideLink to="admin-pages">Admin pages</GuideLink> lists every tab and what to use it for.
      </p>
      <Note tone="info">
        A good habit: open Access Explorer before and after a change. It shows what a role or a user ends up
        with, and through which group.
      </Note>
    </Section>
  </div>
);

/** User, Role, Endpoints Group, Permission Objects; Role, Asset Group, Assets; Company on the user. */
export const AccessModelDiagram: React.FC = () => (
  <Diagram
    label="How access works"
    minWidth={820}
    caption="Red: people. Blue: what they can open and call. Green: which assets they see. The company is a label on the user."
  >
    <div
      className="grid items-center gap-y-5"
      style={{
        gridTemplateColumns:
          "minmax(130px,1fr) minmax(76px,104px) minmax(104px,0.8fr) minmax(96px,132px) minmax(124px,1fr) minmax(64px,84px) minmax(200px,1.4fr)",
      }}
    >
      <div className="row-span-2">
        <Node title="User" caption="User Setup tab" icon={FiUser} tone="people">
          <div className="rounded-md border border-dashed border-gray-300 px-2 py-1 text-xs leading-4 text-gray-600">
            <span className="font-medium text-gray-800">Company</span>
            <span className="block">a label: grants nothing</span>
          </div>
        </Node>
      </div>
      <div className="row-span-2 self-stretch">
        <Arrow label="has one or more" />
      </div>
      <div className="row-span-2 self-stretch py-2">
        <Node
          title="Role"
          caption="Role tab, in one domain"
          icon={FiUsers}
          tone="people"
          className="flex h-full flex-col justify-center"
        />
      </div>

      <div className="self-stretch">
        <Arrow label="holds at read, write or admin" />
      </div>
      <Node title="Endpoints Group" caption="Endpoints Group tab" icon={FiLayers} tone="endpoint" />
      <div className="self-stretch">
        <Arrow label="contains" />
      </div>
      <Node title="Permission Objects" caption="Permission Objects tab" icon={FiKey} tone="endpoint">
        <NodeItem name="API">a route and service, with a level per method</NodeItem>
        <NodeItem name="UI page (UI_ELEMENT)">a page or tab path</NodeItem>
        <NodeItem name="FileCenter folder (BLOB_FOLDER)">one asset's folder</NodeItem>
      </Node>

      <div className="self-stretch">
        <Arrow label="holds" />
      </div>
      <Node title="Asset Group" caption="Asset Group tab, a collection" icon={FiLayers} tone="asset" />
      <div className="self-stretch">
        <Arrow label="contains" />
      </div>
      <Node title="Assets" caption="Asset tab" icon={FiBox} tone="asset" />
    </div>
  </Diagram>
);

const LevelLadder: React.FC = () => (
  <div className="flex flex-wrap items-center gap-2 text-xs" aria-label="Access levels, lowest first">
    {["read", "write", "admin"].map((level, i) => (
      <React.Fragment key={level}>
        {i > 0 && <span className="text-gray-400">&lt;</span>}
        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
          {level}
        </span>
      </React.Fragment>
    ))}
  </div>
);

export const HowAccessWorks: React.FC = () => (
  <div className="space-y-8">
    <Lead>
      Read this when you need the model behind the other sections: how a user reaches a page, an API, a
      folder or an asset.
    </Lead>

    <AccessModelDiagram />

    <Section title="Reading the diagram">
      <NumberedList>
        <li>A user has one or more roles (the role-user mapping). You set them in User Setup or on the Role tab.</li>
        <li>
          Each role holds endpoint groups (the Endpoints Group tab). A role holds a group in a domain at an
          access level: read, write or admin.
        </li>
        <li>Each endpoint group contains permission objects.</li>
        <li>
          A role also holds asset groups (the Asset Group tab, also called collections). Asset groups contain
          assets. A user sees an asset only if one of their roles holds an asset group that contains it.{" "}
          <Code>BETM-All-Assets</Code> is the group most roles use.
        </li>
        <li>A user also carries a company. The company is a label. It grants nothing by itself.</li>
      </NumberedList>
    </Section>

    <Section title="Permission object types">
      <Table minWidth={560}>
        <Thead cols={["Type", "What it is"]} />
        <Tbody>
          <tr>
            <td className={TD}><Code>API</Code></td>
            <td className={TD}>A route path plus a service.</td>
          </tr>
          <tr>
            <td className={TD}><Code>UI_ELEMENT</Code></td>
            <td className={TD}>
              A page or tab path, such as <Code>/administration/user-setup</Code>.
            </td>
          </tr>
          <tr>
            <td className={TD}><Code>BLOB_FOLDER</Code></td>
            <td className={TD}>A FileCenter folder for one asset.</td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Levels">
      <LevelLadder />
      <p>
        For an API object, method access says which level each HTTP method needs. Defaults for a new API
        object: GET is read, POST is write, PUT is write, PATCH is write and DELETE is admin. The role's level
        on the group must be at least the method's level. See <GuideLink to="method-access">Method access</GuideLink>.
      </p>
    </Section>

    <Note tone="info">Only the <Code>amp</Code> and <Code>mobile</Code> domains are managed on these pages.</Note>
  </div>
);

interface TabRow {
  tab: string;
  holds: React.ReactNode;
  use: React.ReactNode;
}

const ASSET_TABS: TabRow[] = [
  { tab: "Assets > Asset", holds: "Asset records.", use: "Create or edit an asset. Onboarding's New asset does the same." },
  {
    tab: "Assets > Company",
    holds: "Companies, their owner assets and their client email domains.",
    use: <>Add a client and its email domains. See <GuideLink to="onboarding-company">Onboarding a company</GuideLink>.</>,
  },
  { tab: "Assets > Participant", holds: "Participant accounts: company, account name and ISO.", use: "Add or edit a participant account." },
  { tab: "Assets > Resource", holds: "Each asset's resources.", use: "Add the resources of a new asset." },
  {
    tab: "Assets > Onboarding",
    holds:
      "A checklist per asset: record exists, in BETM-All-Assets, FileCenter folders, resources and Outage Management (a choice). New asset, Fix all and Refresh my access.",
    use: <>Make a new asset visible and upload-ready. See <GuideLink to="onboarding-an-asset">Onboarding an asset</GuideLink>.</>,
  },
];

const ENTITLEMENT_TABS: TabRow[] = [
  {
    tab: "Entitlements > Asset Group",
    holds: "Which assets are in each group, and their FileCenter folder permissions.",
    use: <>Create a client's asset group. See <GuideLink to="onboarding-asset-group">Onboarding an asset group</GuideLink>.</>,
  },
  {
    tab: "Entitlements > User Setup",
    holds: 'Users and their roles. Refresh access for one user, an "Inactive 90+ days" filter, and per-user activity by month.',
    use: <>Invite a user or change their roles. See <GuideLink to="onboarding-user">Onboarding a user</GuideLink>.</>,
  },
  {
    tab: "Entitlements > Role",
    holds: "Roles, their users, their endpoint groups and their asset groups.",
    use: <>Create a client's role. See <GuideLink to="onboarding-client">Onboarding a client</GuideLink>.</>,
  },
  {
    tab: "Entitlements > Endpoints Group",
    holds: "Which permission objects a group contains.",
    use: <>Look after a client's FileCenter group. See <GuideLink to="filecenter-folders">FileCenter folders</GuideLink>.</>,
  },
  {
    tab: "Entitlements > Permission Objects",
    holds: "API, UI_ELEMENT and BLOB_FOLDER objects and their method access.",
    use: <>Fix a stored path or a method level. See <GuideLink to="method-access">Method access</GuideLink>.</>,
  },
  {
    tab: "Entitlements > Access Explorer",
    holds:
      "Pick a role or a user and see the pages, APIs (with method levels) and asset groups they end up with, and through which group.",
    use: "Check a change before and after you make it.",
  },
  { tab: "Entitlements > Admin Guide", holds: "This page.", use: "Look up how to do a job." },
];

const TabRows: React.FC<{ rows: TabRow[] }> = ({ rows }) => (
  <>
    {rows.map((row) => (
      <tr key={row.tab}>
        <td className={TD}><TabBadge tab={row.tab} /></td>
        <td className={TD}>{row.holds}</td>
        <td className={TD}>{row.use}</td>
      </tr>
    ))}
  </>
);

const GroupRow: React.FC<{ label: string }> = ({ label }) => (
  <tr className="bg-gray-50">
    <td colSpan={3} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </td>
  </tr>
);

export const AdminPages: React.FC = () => (
  <div className="space-y-6">
    <Lead>Use this to find the right tab for a job.</Lead>
    <Table minWidth={760}>
      <Thead cols={["Tab", "What it holds", "Use it to"]} />
      <Tbody>
        <GroupRow label="Assets" />
        <TabRows rows={ASSET_TABS} />
        <GroupRow label="Entitlements" />
        <TabRows rows={ENTITLEMENT_TABS} />
      </Tbody>
    </Table>
    <p>
      A tab shows only if you hold read, write or admin on its page. See{" "}
      <GuideLink to="start-here">Start here</GuideLink> for what the IT group can do.
    </p>
  </div>
);
